import React from 'react';
import { motion } from 'motion/react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SocialShare } from '@/components/ui/SocialShare';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useRegion } from '@/contexts/RegionContext';
import { LazyImage } from '@/components/ui/LazyImage';

export function LensSocialDrivesPage() {
  const { t } = useTranslation();
  const { regionCode } = useRegion();

  const title = 'Social Drives Lens | Status, Connection & Belonging | FIVE LENS';
  const description = 'Analyse how individuals navigate status, connection, and belonging within teams. Uncover fundamental motivations and unmet needs driving team dynamics.';

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: t('nav.framework', 'The Framework'), href: '/#framework' },
          { label: t('framework.lens4.title', 'Social Drives') }
        ]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-6 flex items-center gap-3">
            <span className="h-[1px] w-8 bg-rule-3"></span>
            <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">Lens 04</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
            Social Drives
          </h1>
          
          <div className="prose prose-lg prose-brand max-w-none text-ink/70/80 leading-relaxed font-light">
            <p className="text-xl mb-4 font-normal text-ink">
              The Social Drives lens surfaces the needs and motivations that influence how individuals behave and interact — often without conscious awareness. Based on the models of Abraham Maslow and Richard Barrett, this lens assesses what truly drives a person at a given moment: from basic safety needs to belonging, recognition, and ultimately purpose.
            </p>
            <p className="mb-8">
              By revealing these deeper motivational patterns, the lens helps individuals and teams understand where energy is flowing, where it’s being blocked, and how behaviour is shaped by underlying needs — not just personality.
            </p>
            
            <LazyImage 
              src="/social-drives-DETAILED.webp" 
              alt="Social Drives lens diagram showing how individuals navigate status, connection, and belonging within teams" 
              className="w-full max-w-md mx-auto my-12 object-contain mix-blend-multiply"
            />
            
            <h3 className="text-2xl font-serif text-ink mb-4">How This Lens Fits Into the FIVE LENS Framework</h3>
            <p className="mb-4">
              While the Enneagram maps how people behave, it doesn’t always explain why. The Social Drives lens answers that question by identifying the core motivators — or unmet needs — behind a person’s actions.
            </p>
            <p className="mb-8">
              By integrating this lens into the FIVE LENS model, practitioners gain insight into the forces that push behaviour beneath the surface. It supports clearer communication, more empathetic leadership, and more aligned team dynamics by addressing what truly matters to individuals at a motivational level.
            </p>

            <h3 className="text-2xl font-serif text-ink mb-4">How It Supports Coaching & Organisational Development</h3>
            <p className="mb-4">
              Understanding what drives someone — and what they’re avoiding — is essential in both coaching and team development. This lens helps coaches uncover what energises or demotivates a client and provides a pathway for shifting behaviour by addressing unmet psychological needs.
            </p>
            <p className="mb-8">
              In organisations, it’s a powerful tool for shaping culture, improving engagement, and understanding how different team members find meaning and motivation. It supports leadership development, onboarding, and values alignment initiatives by helping individuals reconnect with what gives their work purpose.
            </p>

            <div className="bg-white p-8 md:p-12 border border-ground-deep my-12">
              <h3 className="text-2xl font-serif text-ink mb-6">The Hierarchy of Motivations</h3>
              <p className="mb-6">Inspired by Maslow and Barrett's value systems, the FIVE LENS framework evaluates where an individual's primary energy is directed. We categorize these into four primary domains of drive:</p>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Security & Stability</h4>
                  <p className="text-sm">The foundational need for safety, predictability, and structure. When this drive is dominant, individuals prioritize risk mitigation, clear boundaries, and financial or operational certainty.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Belonging & Connection</h4>
                  <p className="text-sm">The relational need for acceptance, inclusion, and community. This drive fuels team cohesion, empathy, and the desire to build trust-based relationships within the workplace.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Esteem & Achievement</h4>
                  <p className="text-sm">The need for recognition, competence, and respect. Individuals driven by esteem focus on mastery, performance metrics, career advancement, and contributing visible value to the organization.</p>
                </div>
                <div>
                  <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-3">Meaning & Purpose</h4>
                  <p className="text-sm">The self-actualizing need to contribute to something larger than oneself. This drive inspires visionary leadership, innovation, and a deep alignment between personal values and corporate mission.</p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-serif text-ink mb-4">Navigating Met vs. Unmet Needs</h3>
            <p className="mb-8">Behavior in the workplace is largely dictated by whether our core Social Drives are being satisfied or threatened. By recognizing these states, leaders can shift their management approach to effectively re-engage their teams.</p>

            <div className="space-y-6 mb-12">
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">When Needs are Threatened</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Energy contracts into self-protection. Employees may become territorial (Security), cliquish or overly compliant (Belonging), fiercely competitive or arrogant (Esteem), or disconnected and cynical (Purpose).</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 pb-6 border-b border-ground-deep/50">
                <div className="md:w-1/3 font-bold text-rule-3">When Needs are Met</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Energy expands into collaboration. Individuals feel safe to take calculated risks, communicate openly, share credit for successes, and mentor others for the greater good of the organization.</p></div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="md:w-1/3 font-bold text-rule-3">The Leadership Opportunity</div>
                <div className="md:w-2/3"><p className="m-0 text-sm">Effective leaders use the Social Drives lens to diagnose the root cause of team friction. Instead of just managing the resulting behavior, they actively design environments that fulfill these underlying human needs.</p></div>
              </div>
            </div>
            

            <SocialShare title={`FIVE LENS Framework: ${t('framework.lens4.title', 'Social Drives')}`} />
          </div>
        </motion.div>
      </div>
    </main>
  );
}
